import type { PublicEchoFeedbackRecord } from "@/server/echoFeedback";
import type {
  EchoflicksSessionFeedback,
  EchoflicksSessionFramingEvent,
} from "../../types";
import { effectiveFeedbackCorrelation } from "../../feedback/framingFeedback";
import {
  correlateFeedbackSceneCandidates,
  isCropRelatedFeedback,
} from "../framingDiagnostics";
import type { FramingSceneView } from "../framingDiagnostics";
import {
  prioritizeFramingScenes,
  type FramingFeedbackSceneSets,
  type FramingScenePriorityTier,
  type PrioritizedFramingScene,
} from "./framingScenePriority";

/**
 * Pure selectors for the first-class Framing & crop section (CROP-WB-005).
 * Metadata only: nothing here touches media, signed URLs, or the inspector.
 */

export type FramingFeedbackCorrelationSummary = FramingFeedbackSceneSets & {
  exact: ReadonlySet<string>;
  derived: ReadonlySet<string>;
  ambiguous: ReadonlySet<string>;
  /** Crop feedback that cannot be trusted to any scene. */
  uncorrelatedCount: number;
  totalCropFeedback: number;
};

export function summarizeFramingFeedback(
  sessionFeedback: readonly EchoflicksSessionFeedback[],
  reviewerReports: readonly PublicEchoFeedbackRecord[],
  framingEvents: readonly EchoflicksSessionFramingEvent[] = [],
): FramingFeedbackCorrelationSummary {
  const exact = new Set<string>();
  const derived = new Set<string>();
  const ambiguous = new Set<string>();
  let uncorrelatedCount = 0;
  let totalCropFeedback = 0;

  const cropReports = reviewerReports.filter((report) =>
    report.categories.some((category) => category.id === "framing_crop"),
  );
  for (const report of cropReports) {
    totalCropFeedback += 1;
    const correlation = effectiveFeedbackCorrelation(report, [
      ...framingEvents,
    ]);
    if (correlation.status === "exact" && correlation.sceneId) {
      exact.add(correlation.sceneId);
      continue;
    }
    if (correlation.sceneId) {
      derived.add(correlation.sceneId);
      continue;
    }
    const frozen = correlation.frozenSnapshot;
    if (frozen && frozen.correlationStatus === "ambiguous") {
      for (const candidate of frozen.candidateSceneIds) {
        ambiguous.add(candidate);
      }
      continue;
    }
    const candidates = correlateFeedbackSceneCandidates(
      [...framingEvents],
      report.timestampSeconds,
    );
    if (candidates.status === "ambiguous") {
      for (const candidate of candidates.candidateSceneIds) {
        ambiguous.add(candidate);
      }
      continue;
    }
    uncorrelatedCount += 1;
  }

  const reviewerIds = new Set(cropReports.map((report) => report.id));
  for (const entry of sessionFeedback) {
    if (!isCropRelatedFeedback(entry)) continue;
    // Reviewer reports mapped into session feedback would double-count.
    if (entry.source_id != null && reviewerIds.has(String(entry.source_id))) {
      continue;
    }
    totalCropFeedback += 1;
    if (entry.scene_id != null) {
      derived.add(String(entry.scene_id));
    } else {
      uncorrelatedCount += 1;
    }
  }

  return { exact, derived, ambiguous, uncorrelatedCount, totalCropFeedback };
}

export type FramingSectionFilter =
  | "all"
  | "crop_feedback"
  | "delivered_subject_loss"
  | "plan_applied_mismatch"
  | "repair_fallback"
  | "partial_evidence"
  | "healthy";

export const FRAMING_SECTION_FILTER_LABELS: Record<
  FramingSectionFilter,
  string
> = {
  all: "All scenes",
  crop_feedback: "Crop feedback",
  delivered_subject_loss: "Delivered subject at risk",
  plan_applied_mismatch: "Plan vs applied",
  repair_fallback: "Repaired / fallback",
  partial_evidence: "Partial evidence",
  healthy: "Healthy",
};

const FILTER_TIERS: Record<
  Exclude<FramingSectionFilter, "all">,
  readonly FramingScenePriorityTier[]
> = {
  crop_feedback: ["exact_feedback"],
  delivered_subject_loss: ["delivered_subject_loss"],
  plan_applied_mismatch: ["plan_applied_mismatch"],
  repair_fallback: ["repair_fallback"],
  partial_evidence: ["partial_evidence"],
  healthy: ["timeline"],
};

export type FramingSectionModel = {
  ranked: PrioritizedFramingScene[];
  counts: Record<FramingSectionFilter, number>;
  feedback: FramingFeedbackCorrelationSummary;
};

export function buildFramingSectionModel(
  scenes: readonly FramingSceneView[],
  feedback: FramingFeedbackCorrelationSummary,
): FramingSectionModel {
  const ranked = prioritizeFramingScenes(scenes, feedback);
  const counts: Record<FramingSectionFilter, number> = {
    all: ranked.length,
    crop_feedback: 0,
    delivered_subject_loss: 0,
    plan_applied_mismatch: 0,
    repair_fallback: 0,
    partial_evidence: 0,
    healthy: 0,
  };
  for (const entry of ranked) {
    for (const [filter, tiers] of Object.entries(FILTER_TIERS) as Array<
      [
        Exclude<FramingSectionFilter, "all">,
        readonly FramingScenePriorityTier[],
      ]
    >) {
      if (tiers.includes(entry.tier)) counts[filter] += 1;
    }
  }
  return { ranked, counts, feedback };
}

export function filterRankedScenes(
  ranked: readonly PrioritizedFramingScene[],
  filter: FramingSectionFilter,
): PrioritizedFramingScene[] {
  if (filter === "all") return [...ranked];
  const tiers = FILTER_TIERS[filter];
  return ranked.filter((entry) => tiers.includes(entry.tier));
}

export const INITIAL_SCENE_ROWS = 4;
